import * as React from "react";
import {Button, Form, Row, Spinner, Table} from "react-bootstrap";
import {
    FeatureGating,
    FeatureGatingDataQuery,
} from "Stores/featureGating";
import {useEffect, useMemo, useState} from "react";
import {ColumnDef, flexRender, getCoreRowModel, PaginationState, useReactTable} from "@tanstack/react-table";
import {Defaults, Utils} from "../../../../common/utils";
import {Row as RowInfo} from "@tanstack/table-core";
import {useFeatureGating} from "@/hooks/feature-gating";
import {EAIClient} from "Services/EAIClient";


interface SchoolFeaturesListTableProps {
    schoolId: string
}

export function SchoolFeaturesList({schoolId}: SchoolFeaturesListTableProps) {
    const [featureGatingDataQuery, setFeatureGatingDataQuery] = useState<FeatureGatingDataQuery | null>(null);
    const {data: featureGatingList, mutate: mutateFeatureGatingList} = useFeatureGating(schoolId);
    const [loading, setLoading] = useState<string[]>([]);
    const [{pageIndex, pageSize}, setPagination] = useState<PaginationState>({ pageIndex: Defaults.tablePage(), pageSize: Defaults.tablePageSize()});
    const pagination = useMemo(() => ({pageIndex, pageSize}), [pageIndex, pageSize]);
    const eaiClient = new EAIClient();

    const columns: ColumnDef<FeatureGating>[] = [
        {
            header: "Enabled",
            accessorKey: "enabled",
            cell: context => {
                const handleOnChange = (enabled: boolean) => {
                    handleEnableFeature(context.row.original, enabled);
                };
                if (loading.includes(context.row.original.feature_ruuid)) {
                    return <Spinner size="sm" variant="success" />;
                }

                return (
                    <Form.Check checked={context.row.original.enabled} onChange={ev => handleOnChange(ev.target.checked)} />
                );
            }
        },
        {
            header: "Group",
            accessorKey: "component"
        },
        {
            header: "Feature",
            accessorKey: "name"
        },
        {
            header: "Description",
            accessorKey: "description"
        },
        {
            header: "Settings",
            accessorKey: "settings",
            cell: context => (<div>{JSON.stringify(context.getValue())}</div>)
        },
        {
            header: "Actions",
            cell: ({row}: {row: RowInfo<FeatureGating>}) => renderActionButton(row.original)
        }
    ];

    const handleEnableFeature = async (feature: FeatureGating, enabled: boolean) => {
        setLoading(prevState => [...prevState, feature.feature_ruuid]);
        setFeatureGatingDataQuery(prevState => {
            if (!prevState) {
                return null;
            }
            return {
                data: prevState?.data.map(f => {
                    if (f.feature_ruuid === feature.feature_ruuid) {
                        return {...f, enabled};
                    }
                    return f;
                }),
                result: prevState?.result
            };
        });
        await Promise.all([
            eaiClient.enableSchoolFeature(schoolId, feature.component, feature.name, enabled),
            new Promise((resolve) => {
                setTimeout(resolve, 320, "spinner");
            })
        ]);
        mutateFeatureGatingList();
        setLoading(prevState => prevState.filter(uuid => uuid !== feature.feature_ruuid));
    };

    const handleReset = (feature: FeatureGating) => {
        return async (ev: React.MouseEvent<HTMLButtonElement>) => {
            await eaiClient.deleteSchoolFeature(schoolId, feature.component, feature.name);
            await mutateFeatureGatingList();
        };
    };

    const renderActionButton = (feature: FeatureGating) => {
        return (
            <Button variant="secondary" onClick={handleReset(feature)} size="sm">Reset</Button>
        );
    };

    const table = useReactTable({
        data: featureGatingDataQuery?.data ?? [],
        columns,
        pageCount: Utils.getPageCount(featureGatingDataQuery?.result.totalCount ?? 0, pageSize),
        state: { pagination },
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    useEffect(() => {
        if (featureGatingList) {
            setFeatureGatingDataQuery({
                data: featureGatingList.data,
                result: {totalCount: featureGatingList?.data.length ?? 0},
            });
        }
    }, [featureGatingList]);

    return (
        <>
            <Row className="list">
                <Table>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {table.getRowModel().rows.length === 0 && (<tr><td colSpan={5} className="text-center">No Features</td></tr>)}
                    </tbody>
                </Table>
            </Row>
            <Row>
                <div className="pagination-bottom">
                    <div className="pagination">
                        <div className="previous d-grid gap-2">
                            <Button disabled={!table.getCanPreviousPage()} onClick={() => table.setPageIndex(table.getPageCount() - 1)}>Previous</Button>
                        </div>
                        <div className="center">
                            <span className="page-info">
                                Page
                                <div className="page-jump">
                                    <Form.Control
                                        type="number"
                                        value={table.getState().pagination.pageIndex + 1}
                                        onChange={e => {
                                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                            const pageIndex = table.getState().pagination.pageIndex;
                                            if (page > pageIndex) {
                                                if (table.getCanNextPage()) {
                                                    table.setPageIndex(page);
                                                }
                                            } else if(page < pageIndex) {
                                                if (table.getCanPreviousPage()) {
                                                    table.setPageIndex(page);
                                                }
                                            }
                                        }}/>
                                </div>
                                of {" "}
                                <span className="total-pages">{table.getPageCount()}</span>
                            </span>
                        </div>
                        <div className="next d-grid gap-2">
                            <Button disabled={!table.getCanNextPage()} onClick={() => table.setPageIndex(table.getPageCount() + 1)}>Next</Button>
                        </div>
                    </div>
                </div>
            </Row>
        </>
    );
}